import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { useModal } from "../../hooks/useModal";
import { useStores } from "../../context/StoresContext";

import { Button, Switch } from "antd";

import { InventarioEditStore } from "./InventarioEditStore";
import { useStoreOperations } from "../../hooks/inventario/useStoreOperations";
import { useAppNotification } from "../../hooks/inventario/useAppNotification";

import { icons } from "../../const/icons";
import { useProductOperations } from "../../hooks/inventario/useProductOperations";
import { SyncOutlined } from "@ant-design/icons";
import { TAB_NAMES } from "../../const/inventario";
import { useProducts } from "../../context/ProductsContext";

/**
 * Representa un item individual en el sidebar.
 * Cambia su apariencia según el tab activo
 *
 * @param {object} item - Data del almacen
 * @param {string} activeTab - Tab activo
 */
export const InventarioSidebarItem = ({ item, activeTab }) => {
  //*  HOOKS
  const { stores, selectedStore, setSelectedStore, setSelectedStoreData } = useStores();
  const { isSearchMode, setSearchTerm, setIsSearchMode } = useProducts();
  const { operations: storeOperations, states: storeStates } = useStoreOperations();
  const { operations: productOperations, states: productStates } = useProductOperations();
  const { isOpen, open, close } = useModal();

  //* notificaciones
  const { contextHolder, notifySuccess, notifyError } = useAppNotification();

  //*  ESTADOS LOCALES
  const [storeToEdit, setStoreToEdit] = useState(null);
  const [isToggling, setIsToggling] = useState(false);

  //*  ESTADOS DERIVADOS
  const isSelected = item.name === selectedStore;
  const isAllCategory = item.name === "Todos";
  const isFetchingProducts = productStates.fetchState.loading && isSelected;
  const isLoadingProducts = productStates.fetchState.loading;
  const currentStore = stores.find((s) => s.id === item.id);
  const currentStatus = currentStore?.active ?? item.active;

  //* Feedback de errores
  useEffect(() => {
    if (storeStates.toggleState.error && isToggling) {
      notifyError({
        title: "Error",
        description: `${storeStates.toggleState.error}`,
      });
      setIsToggling(false);
    }
  }, [storeStates.toggleState.error, isToggling, item.id, notifyError]);

  //*  HANDLERS
  /**
   * Maneja la selección de un almacén.
   */
  const handleOnSelectCategory = async (storeItem) => {
    // Evitar cambio si hay fetch en progreso
    if (isLoadingProducts) {
      return;
    }

    // Si hay búsqueda activa, limpiarla primero
    if (isSearchMode) {
      setSearchTerm("");
      setIsSearchMode(false);
    }

    setSelectedStore(storeItem.name);
    setSelectedStoreData(storeItem);

    try {
      if (storeItem.name === "Todos") {
        await productOperations.fetchProducts();
      } else {
        await productOperations.fetchProductsByStore(storeItem.id);
      }
    } catch (error) {
      console.error("Error al cargar productos:", error);
    }
  };

  /**
   * Maneja el cambio del switch (activo/inactivo)
   */
  const handleToggleStatus = async (checked, e) => {
    e.stopPropagation();
    setIsToggling(true);

    try {
      const result = await storeOperations.updateAlmacen(item.id, {
        name: item.name,
        brand: item.brand,
        active: checked,
      });

      if (result.success) {
        notifySuccess({
          title: "Almacén actualizado",
          description: `Almacén ${checked ? "activado" : "desactivado"}`,
        });
      } else {
        notifyError({
          title: "Error",
          description: `${result.error}`,
        });

        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Error al cambiar status:", error);
      notifyError({
        title: "Error",
        description: error.message || "Error al cambiar estado del almacén",
      });
    } finally {
      setIsToggling(false);
    }
  };

  /**
   * Abre modal de edición
   */
  const handleEdit = (e) => {
    e.stopPropagation();

    setStoreToEdit(item);
    open();
  };

  /**
   * Guarda cambios del almacén editado
   */
  const handleEditStore = async (values) => {
    try {
      const result = await storeOperations.updateAlmacen(item.id, {
        name: values.name,
        brand: item.brand,
        active: values.active,
      });

      if (result.success) {
        await productOperations.fetchProductsByStore(item.id);
        setStoreToEdit(null);
        close();
      } else {
        throw new Error(result.error);
      }

      return result;
    } catch (error) {
      const errorMsg = "Error al actualizar almacén";

      console.error(error.message || errorMsg);

      return {
        success: false,
        error: error.message || errorMsg,
      };
    }
  };

  const handleOnClose = () => {
    close();
    setStoreToEdit(null);
  };

  //  RENDER: Tab Items
  if (activeTab === TAB_NAMES.items) {
    return (
      <Button
        onClick={() => handleOnSelectCategory(item)}
        className={`inv-sidebar-btn ${isSelected && "active"}`}
        disabled={productStates.fetchState.loading}
        aria-pressed={isSelected}
      >
        {/* {item.name} {isFetchingProducts && <SyncOutlined spin />} */}
        <div className="inv-sidebar-btn-content">
          <span className="inv-sidebar-btn-label">{item.name}</span>
          {isFetchingProducts && <SyncOutlined spin className="inv-sidebar-btn-spinner" />}
        </div>
      </Button>
    );
  }

  //  RENDER: Tab Almacenes
  if (activeTab === TAB_NAMES.stores) {
    return (
      <>
        {contextHolder}

        <Button
          type="button"
          onClick={() => handleOnSelectCategory(item)}
          className={`items-container ${isSelected && "active"}`}
          disabled={isLoadingProducts}
          aria-pressed={isSelected}
        >
          {isLoadingProducts ? (
            <div className="scroller">
              <div className="item-title">
                <span>Almacén</span>
                <p>
                  {item.name} {isFetchingProducts && <SyncOutlined spin />}{" "}
                </p>
              </div>
            </div>
          ) : (
            <div className={`scroller ${isAllCategory && "scroller-all"}`}>
              {/* Columna 1: Título */}
              <div className="item-title">
                <span>Almacén</span>
                <p>{item.name}</p>
              </div>

              {/* Columna 2: Contador */}
              {!isAllCategory && (
                <div className="item-counter">
                  <span>N&ordm; Items</span>
                  <p>{String(item.productCount).padStart(2, "0")}</p>
                </div>
              )}

              {/* Columna 3: Status (solo si no es "Todos") */}
              {!isAllCategory && (
                <div className="item-status" onClick={(e) => e.stopPropagation()}>
                  <span>Activo</span>
                  <Switch
                    checked={currentStatus}
                    onChange={handleToggleStatus}
                    loading={storeStates.toggleState.loading || isToggling}
                    disabled={storeStates.toggleState.loading || isToggling}
                    className="react-switch"
                    size="small"
                    style={{
                      backgroundColor: item.active ? "#256d3e" : "#dad8d8ff",
                    }}
                  />
                </div>
              )}

              {/* Columna 4: Editar almacen (solo si no es "Todos") */}
              {!isAllCategory && (
                <div className="item-edit" onClick={(e) => e.stopPropagation()}>
                  <div
                    onClick={handleEdit}
                    className="button-styles-detalais-catalogo btn-edit-store"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleEdit(e);
                      }
                    }}
                    aria-label="Editar almacén"
                  >
                    <img src={icons.edit} alt="Editar almacén" className="edit-icon" />
                  </div>
                </div>
              )}
            </div>
          )}
        </Button>

        <InventarioEditStore
          visible={isOpen}
          onClose={handleOnClose}
          onSubmit={handleEditStore}
          almacen={storeToEdit}
          saving={storeStates.updateState.loading}
        />
      </>
    );
  }
};

InventarioSidebarItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    brand: PropTypes.string,
    active: PropTypes.bool,
    productCount: PropTypes.number,
  }).isRequired,
  activeTab: PropTypes.string,
};
